import { FieldPath, FieldValues, useFormContext } from 'react-hook-form';
import { createInfiniteSearchSelect, FormSelectProps } from '../common/form';
import {
  DataObjectOption,
  useInfiniteSearchDataObjectsQuery,
} from './query/useInfiniteSearchDataObjectsQuery';
import {
  DataObjectFormDataType,
  DataObjectOutputType,
  DataObjectType,
} from './types';
import { OptionType } from '../common/queryClient';
import { useCallback, useMemo } from 'react';

export interface DataObjectFormAutoCompleteProps<
  FormData extends FieldValues,
  Name extends FieldPath<FormData> = FieldPath<FormData>,
  Value extends FormData[Name] = FormData[Name],
> extends FormSelectProps<FormData, Name, Value> {
  defaultOption?: OptionType;
  dataObjectType?: DataObjectType;
  outputTypeCodes?: DataObjectOutputType[];
}

const DataObjectInfiniteSelect = createInfiniteSearchSelect(
  useInfiniteSearchDataObjectsQuery,
);

export const DataObjectFormAutoComplete = <
  FormData extends FieldValues,
  Name extends FieldPath<FormData> = FieldPath<FormData>,
  Value extends FormData[Name] = FormData[Name],
>(
  props: DataObjectFormAutoCompleteProps<FormData, Name, Value>,
) => {
  const { dataObjectType, onChange, outputTypeCodes, ...rest } = props;
  const form = useFormContext<DataObjectFormDataType>();

  const searchOptions = useMemo(
    () => ({
      dataObjectType,
      outputTypeCodes,
    }),
    [dataObjectType, outputTypeCodes],
  );
  const handleChange = useCallback(
    (value: Value, option: DataObjectOption | DataObjectOption[]) => {
      const selectedOption = Array.isArray(option) ? option[0] : option;
      if (selectedOption?.item) {
        const item = selectedOption.item;
        const opts = { shouldDirty: false };
        if (item.isSystemic) {
          form.setValue('groupAndSubgroupId', undefined, { shouldDirty: true });
        } else {
          form.setValue('groupAndSubgroupId', item.groupId?.toString(), {
            shouldDirty: true,
          });
        }
        form.setValue('domainId', item.domainId ?? undefined, opts);
        form.setValue(
          'dataProviderGroupId',
          item.dataProviderGroupId ?? undefined,
          opts,
        );
        form.setValue('frequencyId', item.frequencyId ?? undefined, opts);
        form.setValue('accessGroupId', item.accessGroupId ?? undefined, opts);
        form.setValue('confidential', item.confidential ?? false, opts);
        form.setValue('responsibleId', item.responsibleId ?? null!, opts);
        form.setValue(
          'responsibleUsername',
          item.responsibleUsername ?? '',
          opts,
        );
      }
      onChange?.(value, option);
    },
    [form, onChange],
  );

  return (
    <DataObjectInfiniteSelect
      {...rest}
      styles={{
        popup: { root: { width: '300px' } },
      }}
      searchOptions={searchOptions}
      onChange={handleChange}
    />
  );
};
